import type { Chart, ChartType, Plugin } from 'chart.js';
import { _isPointInArea } from 'chart.js/helpers';

type Position = { x: number; y: number };

/** The part of a Chart.js chart the reset needs; a `Chart` with the zoom plugin satisfies it. */
export interface DoubleClickResetTarget {
    chartArea: Chart['chartArea'];
    isZoomedOrPanned(): boolean;
    resetZoom(): void;
}

/**
 * Resets the zoom when the double click lands inside the plotting area of a zoomed or panned
 * chart. A double click on the legend, the axes or an unzoomed chart does nothing.
 */
export function resetZoomOnDoubleClick(chart: DoubleClickResetTarget, position: Position): void {
    if (!_isPointInArea(position, chart.chartArea)) return;
    if (!chart.isZoomedOrPanned()) return;
    chart.resetZoom();
}

/**
 * Listens for double clicks on `canvas` and resets `chart` through `resetZoomOnDoubleClick`,
 * with `toPosition` turning the event into chart coordinates. Returns the function that stops
 * listening.
 */
export function listenForDoubleClick(
    canvas: HTMLCanvasElement,
    chart: DoubleClickResetTarget,
    toPosition: (event: MouseEvent) => Position
): () => void {
    const onDoubleClick = (event: MouseEvent) => resetZoomOnDoubleClick(chart, toPosition(event));
    canvas.addEventListener('dblclick', onDoubleClick);
    return () => canvas.removeEventListener('dblclick', onDoubleClick);
}

/**
 * The event's position in chart coordinates. The canvases BaseChart draws have no padding or
 * border, so the canvas box is the chart's coordinate space (CSS pixels, as `chartArea` is).
 */
export function canvasPosition(canvas: HTMLCanvasElement, event: MouseEvent): Position {
    const box = canvas.getBoundingClientRect();
    return { x: event.clientX - box.left, y: event.clientY - box.top };
}

// Chart.js uninstalls a plugin after destroy has already nulled `chart.canvas`, so the
// listener is removed through what `listenForDoubleClick` returned at install.
const stopListening = new WeakMap<object, () => void>();

/**
 * Double click inside a chart resets its zoom, the same as "Reset zoom" in the chart's menu.
 * Chart.js does not listen for `dblclick` (it is not in its default `events`), so the plugin
 * listens on the canvas itself, from install until the chart is destroyed. BaseChart passes it
 * to every line and bar chart; a chart that does not zoom is never zoomed, so it ignores it.
 */
export function createDoubleClickResetPlugin<TType extends ChartType>(): Plugin<TType> {
    return {
        id: 'doubleClickReset',
        install(chart) {
            const canvas = chart.canvas;
            stopListening.set(
                chart,
                listenForDoubleClick(canvas, chart, (event) => canvasPosition(canvas, event))
            );
        },
        uninstall(chart) {
            stopListening.get(chart)?.();
            stopListening.delete(chart);
        },
    };
}
